package dev.lingvo.site.components.review

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.ReviewAction
import dev.lingvo.shared.ReviewItemDto
import dev.lingvo.shared.TranslationDto
import dev.lingvo.shared.SourceChangeDto
import dev.lingvo.shared.SourceReviewDecisionRequest
import dev.lingvo.site.api.API_JSON
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.components.editor.unavailableSourceReviewMessage
import dev.lingvo.site.components.source.hasIdentity
import dev.lingvo.site.components.editor.isTextEntry
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCircleCheckIcon
import dev.lingvo.site.ds.LCommentIcon
import dev.lingvo.site.ds.LTextArea
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.serialization.json.JsonObject
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent

/**
 * The three decisions, the chords that fire them, and the mapping from a refusal to a sentence.
 *
 * Extracted from the page so the coordinator is a coordinator: this file holds what a decision *is*
 * and how one is sent, and knows nothing about queues, cursors or scopes.
 */

internal const val REVIEW_REQUEST_CHANGES_TRIGGER_ID = "lingvo-review-request-changes-trigger"
internal const val REVIEW_REQUEST_CHANGES_FIELD_ID = "lingvo-review-request-changes-field"

/**
 * How a decision ended, as four outcomes rather than a nullable string.
 *
 * The distinction that matters is between news and failure. Losing a race is news: somebody else
 * got here first, nothing is wrong, and the reviewer should move on. A save that failed is theirs
 * to act on. Both used to arrive as the same `String?`, which meant the caller could only advance
 * on `null` — so the message reading "Moving on." pinned the reviewer on the very card it was
 * telling them they had left.
 */
internal sealed interface Decision {
    data object Done : Decision

    /** Somebody else got here first. Not this reviewer's failure, and not a reason to stay put. */
    data object AlreadyDecided : Decision

    data class Problem(val text: String) : Decision

    /**
     * The string moved while it was being read, and here is what it says now.
     *
     * Its own case rather than a `Problem` with better wording: the difference between the two is
     * that this one is *recoverable in place*. A reviewer who can see the new text can approve it
     * without losing their position in the queue, which is the whole reason the refusal carries a
     * body at all.
     */
    data class Stale(val current: TranslationDto) : Decision
    data class SourceChanged(val change: SourceChangeDto) : Decision
    data class SourceUnavailable(val text: String) : Decision
}

/** Sends one decision, and turns the failures that matter into sentences. */
internal suspend fun decide(
    slug: String,
    locale: String,
    item: ReviewItemDto,
    action: ReviewAction,
    note: String?,
    guardedMode: Boolean = false,
): Decision = try {
    if (!reviewSourceClaimAvailable(item, guardedMode = guardedMode)) Decision.SourceUnavailable(unavailableSourceReviewMessage()) else {
        if (sourceReviewRequired(item, guardedMode)) ApiClient.reviewDecisionForSource(slug, locale, item.keyId,
            SourceReviewDecisionRequest(checkNotNull(item.sourceSnapshot).view, checkNotNull(item.targetRevision), action, note))
        else ApiClient.reviewDecision(slug, locale, item.keyId, action, note, item.updatedAt)
        Decision.Done
    }
} catch (failure: ApiException) {
    val result = reviewFailure(failure.code, failure.message, failure.details)
    when {
        result is Decision.SourceChanged && !result.change.hasIdentity(item.keyId, locale) -> Decision.SourceUnavailable(unavailableSourceReviewMessage())
        sourceReviewRequired(item, guardedMode) && failure.status in listOf(404, 501) -> Decision.SourceUnavailable(unavailableSourceReviewMessage())
        sourceReviewRequired(item, guardedMode) && failure.status in listOf(401, 403) -> Decision.SourceUnavailable(failure.message)
        else -> result
    }
}

/** Busy is a resource: even route cancellation must release it before this composition disappears. */
internal suspend fun runReviewAction(
    onBusyChange: (Boolean) -> Unit,
    action: suspend () -> Unit,
) {
    onBusyChange(true)
    try {
        action()
    } finally {
        onBusyChange(false)
    }
}

/**
 * Split out from the `catch` so the mapping can be tested without a server.
 *
 * [details] is the refusal's structured body. `stale_translation` carries the translation as it
 * actually stands, and reading it is what turns "reload and lose your place" into a choice — but a
 * refusal with no body still has to degrade to a sentence, because an older server sends one.
 */
internal fun reviewFailure(
    code: String?,
    message: String?,
    details: JsonObject = JsonObject(emptyMap()),
): Decision = when (code) {
    "already_reviewed" -> Decision.AlreadyDecided
    // A malformed body is the same case as no body, not a crash on the reviewer's screen. The
    // `runCatching` is deliberate and narrow: it wraps one non-suspending decode, which is the one
    // shape the house rule against it does not cover.
    "source_changed", "source_view_unavailable" -> details["sourceChange"]
        ?.let { runCatching { API_JSON.decodeFromJsonElement(SourceChangeDto.serializer(), it) }.getOrNull() }
        ?.let { Decision.SourceChanged(it) }
        ?: Decision.SourceUnavailable(unavailableSourceReviewMessage())
    "stale_translation" -> details["sourceChange"]
        ?.let { runCatching { API_JSON.decodeFromJsonElement(SourceChangeDto.serializer(), it) }.getOrNull() }
        ?.let { Decision.SourceChanged(it) }
        ?: details["current"]
        ?.let { runCatching { API_JSON.decodeFromJsonElement(TranslationDto.serializer(), it) }.getOrNull() }
        ?.let { Decision.Stale(it) }
        ?: Decision.Problem(
            tr(
                "review.review_decisions.this_string_changed_while_you_were",
                "This string changed while you were reading it. Reload the queue.",
            ),
        )
    else -> Decision.Problem(
        message ?: tr(
            "review.review_decisions.that_didnt_go_through_try_again",
            "That didn't go through. Try again in a moment.",
        ),
    )
}

/**
 * Route one decision to the right piece of page state.
 *
 * Shared by Approve and Send back so the two cannot drift: a race handled one way in one handler
 * and another way in the other is how "Moving on." ended up not moving on.
 */
internal inline fun applyDecision(
    decision: Decision,
    onAdvance: () -> Unit,
    onNotice: (String?) -> Unit,
    onProblem: (String?) -> Unit,
    onStale: (TranslationDto) -> Unit,
    onSourceChanged: (SourceChangeDto) -> Unit = {},
    onSourceUnavailable: () -> Unit = {},
) {
    when (decision) {
        Decision.Done -> {
            onProblem(null)
            onNotice(null)
            onAdvance()
        }
        Decision.AlreadyDecided -> {
            onProblem(null)
            onNotice(
                tr(
                    "review.review_decisions.already_reviewed_by_someone_else_moving",
                    "Already reviewed by someone else — moving on.",
                ),
            )
            onAdvance()
        }
        is Decision.Problem -> {
            onNotice(null)
            onProblem(decision.text)
        }
        is Decision.Stale -> {
            onNotice(null)
            onProblem(null)
            onStale(decision.current)
        }
        is Decision.SourceChanged -> {
            onNotice(null)
            onProblem(null)
            onSourceChanged(decision.change)
        }
        is Decision.SourceUnavailable -> {
            onNotice(null)
            onProblem(decision.text)
            onSourceUnavailable()
        }
    }
}

/** Whether approving *this* string, in this state, is something the server would accept. */
internal fun reviewApprovalAvailable(
    item: ReviewItemDto,
    current: TranslationDto?,
    canApprove: Boolean,
    busy: Boolean,
    asking: Boolean,
    guardedMode: Boolean = false,
): Boolean {
    if (!canApprove || busy || asking) return false
    if (!reviewSourceClaimAvailable(item, current, guardedMode)) return false
    return if (current == null) {
        !item.blocked && reviewPayloadIsRepresentable(item)
    } else {
        staleReviewPayloadIsRepresentable(item, current)
    }
}

@Composable
internal fun ReviewApprovalButton(
    label: String,
    item: ReviewItemDto,
    current: TranslationDto?,
    canApprove: Boolean,
    busy: Boolean,
    onApprove: () -> Unit,
    guardedMode: Boolean = false,
) {
    LButton(
        label = label,
        onClick = onApprove,
        enabled = reviewApprovalAvailable(item, current, canApprove, busy, asking = false, guardedMode = guardedMode),
        leadingIcon = { LCircleCheckIcon() },
        loading = busy,
    )
}

@Composable
internal fun ReviewRequestChangesTrigger(
    label: String,
    enabled: Boolean,
    onOpen: () -> Unit,
) {
    LButton(
        label,
        onOpen,
        modifier = Modifier.attr("id", REVIEW_REQUEST_CHANGES_TRIGGER_ID),
        enabled = enabled,
        variant = LButtonVariant.SECONDARY,
        leadingIcon = { LCommentIcon() },
    )
}

@Composable
internal fun ReviewRequestChangesForm(
    note: String,
    onNoteChange: (String) -> Unit,
    busy: Boolean,
    noteLabel: String,
    placeholder: String,
    sendLabel: String,
    cancelLabel: String,
    onSend: () -> Unit,
    onCancel: () -> Unit,
) {
    LaunchedEffect(Unit) {
        window.requestAnimationFrame {
            (document.getElementById(REVIEW_REQUEST_CHANGES_FIELD_ID)
                ?.querySelector("textarea") as? HTMLElement)?.focus()
        }
    }

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.SM)) {
            LTextArea(
                note,
                onNoteChange,
                Modifier.fillMaxWidth().attr("id", REVIEW_REQUEST_CHANGES_FIELD_ID),
                label = noteLabel,
                placeholder = placeholder,
            )
            Row(Modifier.gap(Space.SM)) {
                LButton(
                    sendLabel,
                    onSend,
                    enabled = note.isNotBlank() && !busy,
                    leadingIcon = { LCommentIcon() },
                    loading = busy,
                )
                LButton(
                    cancelLabel,
                    {
                        onCancel()
                        window.requestAnimationFrame {
                            window.requestAnimationFrame {
                                (document.getElementById(REVIEW_REQUEST_CHANGES_TRIGGER_ID) as? HTMLElement)
                                    ?.focus()
                            }
                        }
                    },
                    variant = LButtonVariant.GHOST,
                )
            }
        }
    }
}

/**
 * The chords the page header has always promised.
 *
 * **Nothing was wired.** A translator arriving from the editor — where the full implementation and
 * a discoverable `?` popover live — pressed `a` and got silence, a hundred times over a hundred
 * reviews, each one a mouse round-trip to a button that moves as the card above it changes height.
 *
 * A null handler means that action is currently refused, so the guards live in one place with the
 * buttons rather than being restated here and drifting.
 */
@Composable
internal fun ReviewShortcuts(
    onApprove: (() -> Unit)?,
    onRequestChanges: (() -> Unit)?,
    onSkip: (() -> Unit)?,
) {
    // Keyed on the handlers themselves, not on the booleans behind them, and deliberately so.
    // These lambdas close over `item` and `cursor`; they are new objects on every recomposition,
    // which means the listener is rebound every time and can never fire against a stale card. Key
    // this on `queue.canApprove`/`busy` instead and the listener survives a card change holding the
    // previous item — pressing `a` twice quickly then decides the same string twice. Rebinding one
    // event listener per recomposition of a single card costs nothing next to that.
    DisposableEffect(onApprove, onRequestChanges, onSkip) {
        val listener = EventListener { rawEvent ->
            val event = rawEvent as? KeyboardEvent ?: return@EventListener
            // A dialog owns the keyboard while it is open.
            if (document.querySelector("[aria-modal='true']") != null) return@EventListener
            // And so does whatever is being typed into — including the note field one card down.
            if (document.activeElement.isTextEntry()) return@EventListener
            if (event.altKey) return@EventListener

            val command = event.metaKey || event.ctrlKey
            when {
                // The one chord with genuine transfer from the editor, and it means the same thing.
                command && event.shiftKey && event.key == "Enter" -> onApprove?.let {
                    event.preventDefault()
                    it()
                }
                command -> Unit
                event.key.equals("a", ignoreCase = true) -> onApprove?.let {
                    event.preventDefault()
                    it()
                }
                event.key.equals("r", ignoreCase = true) -> onRequestChanges?.let {
                    event.preventDefault()
                    it()
                }
                event.key.equals("s", ignoreCase = true) -> onSkip?.let {
                    event.preventDefault()
                    it()
                }
            }
        }
        window.addEventListener("keydown", listener)
        onDispose { window.removeEventListener("keydown", listener) }
    }
}
